// Guided coach-marks tour — shown once after the first-visit wizard, and
// replayable via the "Comment ça marche" button in the profile modal.
// Points at each header control in turn (deck, mode, diff, tag filter,
// theme) plus the footer card actions and the profile button. On mobile
// the relevant controls live inside the hamburger popover, so the tour
// opens the popover automatically before highlighting those targets.

import { STORAGE_KEYS } from "../config/storage-keys";
import { t } from "../i18n";
import { byId } from "./dom";
import { closeHeaderMenu, openHeaderMenu } from "./header-menu";

interface Step {
  /** Element id OR CSS selector for the target. When absent, the step
   *  is a centered intro/summary slide with no highlight. */
  target?: string;
  titleKey: string;
  descKey: string;
  /** Open the header hamburger popover before highlighting (mobile only). */
  inMenu?: boolean;
}

// Hamburger items stay in the same order as they appear inside
// #headerOverflow in index.html, so the tour walks the popover
// top-to-bottom instead of jumping around. Non-menu steps (level pill,
// footer actions, profile) bookend the hamburger run.
const STEPS: Step[] = [
  { titleKey: "tour_intro_title", descKey: "tour_intro_desc" },
  { target: "#userLevelBtn", titleKey: "tour_s1_title", descKey: "tour_s1_desc" },
  { target: "#listDropdownBtn", titleKey: "tour_s2_title", descKey: "tour_s2_desc", inMenu: true },
  { target: "#deckSettingsMenuBtn", titleKey: "tour_s4_title", descKey: "tour_s4_desc", inMenu: true },
  { target: "#modeDropdownBtn", titleKey: "tour_s3_title", descKey: "tour_s3_desc", inMenu: true },
  { target: "#addCardBtn", titleKey: "tour_s10_title", descKey: "tour_s10_desc", inMenu: true },
  { target: "#searchBtn", titleKey: "tour_s11_title", descKey: "tour_s11_desc", inMenu: true },
  { target: "#aideBtn", titleKey: "tour_s9_title", descKey: "tour_s9_desc", inMenu: true },
  { target: ".footer-actions", titleKey: "tour_s7_title", descKey: "tour_s7_desc" },
  { target: "#userBtn", titleKey: "tour_s8_title", descKey: "tour_s8_desc" },
];

const PAD = 6;
const GAP = 12;
const MARGIN = 12;

let overlayEl: HTMLDivElement | null = null;
let highlightEl: HTMLDivElement | null = null;
let tooltipEl: HTMLDivElement | null = null;
let currentIdx = 0;
let activeSteps: Step[] = [];
let resizeHandler: (() => void) | null = null;
let keyHandler: ((e: KeyboardEvent) => void) | null = null;

/** Whether inMenu steps need to auto-open the hamburger. Now that the
 *  popover is used on every viewport, this is always true — kept as a
 *  function so the call sites stay readable. */
function needsMenu(): boolean {
  return true;
}

function lookupEl(sel: string): HTMLElement | null {
  return sel.startsWith("#")
    ? byId(sel.slice(1))
    : document.querySelector<HTMLElement>(sel);
}

/** Stepwise visibility check — runs AFTER the menu has been opened (when
 *  needed) so items inside the hamburger popover aren't treated as
 *  hidden on mobile. */
function resolveTarget(sel: string): HTMLElement | null {
  const el = lookupEl(sel);
  if (!el) return null;
  if (el.classList.contains("hidden") || el.offsetParent === null) return null;
  return el;
}

/** Pre-flight filter — drops steps whose *direct* target has `.hidden`
 *  (e.g. listDropdown when only one deck, userBtn before a name is set)
 *  or an ancestor with `.hidden`. Intro/summary steps (no target) always
 *  pass. Items inside the hamburger popover are NOT dropped here: on
 *  mobile the popover is hidden until we open it, and `inMenu` steps
 *  handle that themselves at render time. */
function filterSteps(): Step[] {
  return STEPS.filter((s) => {
    if (!s.target) return true;
    const el = lookupEl(s.target);
    if (!el) return false;
    if (el.classList.contains("hidden")) return false;
    for (
      let p: HTMLElement | null = el.parentElement;
      p && p !== document.body;
      p = p.parentElement
    ) {
      if (p.id === "headerOverflow") break;
      if (p.classList.contains("hidden")) return false;
    }
    return true;
  });
}

function ensureMenuForStep(step: Step): void {
  if (!needsMenu()) return;
  if (step.inMenu) openHeaderMenu();
  else closeHeaderMenu();
}

function buildOverlay(): void {
  overlayEl = document.createElement("div");
  overlayEl.id = "tourOverlay";
  overlayEl.className = "tour-overlay";
  highlightEl = document.createElement("div");
  highlightEl.className = "tour-highlight";
  tooltipEl = document.createElement("div");
  tooltipEl.className = "tour-tooltip";
  overlayEl.appendChild(highlightEl);
  overlayEl.appendChild(tooltipEl);
  // Swallow clicks so the document-level outside-click handler in
  // header-menu.ts doesn't close the hamburger we just opened when the
  // user clicks a tour button.
  overlayEl.addEventListener("click", (e) => e.stopPropagation());
  document.body.appendChild(overlayEl);
}

function teardownOverlay(): void {
  if (overlayEl) {
    overlayEl.remove();
    overlayEl = null;
    highlightEl = null;
    tooltipEl = null;
  }
  if (resizeHandler) {
    window.removeEventListener("resize", resizeHandler);
    window.removeEventListener("scroll", resizeHandler, true);
    resizeHandler = null;
  }
  if (keyHandler) {
    document.removeEventListener("keydown", keyHandler);
    keyHandler = null;
  }
}

function positionFor(target: HTMLElement): void {
  if (!highlightEl || !tooltipEl) return;
  const rect = target.getBoundingClientRect();
  const hx = rect.left - PAD;
  const hy = rect.top - PAD;
  const hw = rect.width + PAD * 2;
  const hh = rect.height + PAD * 2;
  highlightEl.style.left = hx + "px";
  highlightEl.style.top = hy + "px";
  highlightEl.style.width = hw + "px";
  highlightEl.style.height = hh + "px";

  // Tooltip: prefer below, fall back above. Clamp horizontally.
  const tw = tooltipEl.offsetWidth;
  const th = tooltipEl.offsetHeight;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const targetCx = rect.left + rect.width / 2;
  let tx = targetCx - tw / 2;
  tx = Math.max(MARGIN, Math.min(tx, vw - tw - MARGIN));
  let ty = rect.bottom + GAP;
  if (ty + th > vh - MARGIN) {
    ty = rect.top - GAP - th;
  }
  if (ty < MARGIN) ty = MARGIN;
  tooltipEl.style.left = tx + "px";
  tooltipEl.style.top = ty + "px";
}

function renderTooltipBody(): void {
  const step = activeSteps[currentIdx];
  if (!step || !tooltipEl) return;
  const isLast = currentIdx === activeSteps.length - 1;
  const isFirst = currentIdx === 0;
  tooltipEl.innerHTML =
    `<div class="tour-step-count">${currentIdx + 1}/${activeSteps.length}</div>` +
    `<h3 class="tour-title">${t(step.titleKey) as string}</h3>` +
    `<p class="tour-desc">${t(step.descKey) as string}</p>` +
    `<div class="tour-nav">` +
    `<button type="button" class="tour-skip-btn">${t("tour_skip") as string}</button>` +
    `<div class="tour-nav-right">` +
    (isFirst ? "" : `<button type="button" class="tour-back-btn">${t("tour_back") as string}</button>`) +
    `<button type="button" class="tour-next-btn">${t(isLast ? "tour_done" : "tour_next") as string}</button>` +
    `</div></div>`;
  tooltipEl.querySelector(".tour-skip-btn")?.addEventListener("click", finishTour);
  tooltipEl.querySelector(".tour-back-btn")?.addEventListener("click", () => {
    if (currentIdx > 0) {
      currentIdx--;
      renderStep();
    }
  });
  tooltipEl.querySelector(".tour-next-btn")?.addEventListener("click", () => {
    if (currentIdx >= activeSteps.length - 1) {
      finishTour();
    } else {
      currentIdx++;
      renderStep();
    }
  });
}

function renderStep(): void {
  const step = activeSteps[currentIdx];
  if (!step || !tooltipEl || !highlightEl || !overlayEl) return;
  ensureMenuForStep(step);

  // Intro / summary slide — no element to highlight, tooltip centered
  // on screen, overlay carries its own backdrop since there's no
  // highlight box-shadow trick to dim the surroundings.
  if (!step.target) {
    overlayEl.classList.add("tour-no-highlight");
    highlightEl.style.display = "none";
    renderTooltipBody();
    // Inline styles win over CSS centering, so clear them.
    tooltipEl.style.left = "";
    tooltipEl.style.top = "";
    return;
  }

  overlayEl.classList.remove("tour-no-highlight");
  highlightEl.style.display = "";
  // Let the DOM paint so the menu open anim/layout settles before
  // measuring. Double-rAF: the first tick applies the class change,
  // the second guarantees layout has been recomputed so offsetParent
  // is set for items that just appeared (menu items on mobile).
  requestAnimationFrame(() => requestAnimationFrame(() => {
    const target = resolveTarget(step.target!);
    if (!target) {
      currentIdx++;
      if (currentIdx >= activeSteps.length) finishTour();
      else renderStep();
      return;
    }
    renderTooltipBody();
    positionFor(target);
  }));
}

function finishTour(): void {
  teardownOverlay();
  if (needsMenu()) closeHeaderMenu();
  localStorage.setItem(STORAGE_KEYS.INTRO, "1");
}

function startTour(): void {
  activeSteps = filterSteps();
  if (skipIntroNext) {
    activeSteps = activeSteps.filter((s) => s.target != null);
  }
  if (activeSteps.length === 0) return;
  currentIdx = 0;
  if (overlayEl) teardownOverlay();
  buildOverlay();
  resizeHandler = () => {
    const step = activeSteps[currentIdx];
    if (!step || !step.target) return;
    const target = resolveTarget(step.target);
    if (target) positionFor(target);
  };
  window.addEventListener("resize", resizeHandler);
  window.addEventListener("scroll", resizeHandler, true);
  keyHandler = (e: KeyboardEvent) => {
    if (e.key === "Escape") finishTour();
  };
  document.addEventListener("keydown", keyHandler);
  renderStep();
}

export interface OpenTourOptions {
  /** Drop the intro/summary slide from the step list. Used on first
   *  visit after the wizard, since the dedicated intro screen already
   *  covered that ground; profile replays default to false so the tour
   *  reads end-to-end. */
  skipIntro?: boolean;
}

let skipIntroNext = false;

/** Public entry. If the card screen hasn't painted yet (right after
 *  wizard finish, while loadList is still resolving), poll briefly so
 *  `.footer-actions` isn't filtered out of the step list. */
export function openTour(opts: OpenTourOptions = {}): void {
  skipIntroNext = opts.skipIntro === true;
  const cardScreen = byId("cardScreen");
  if (!cardScreen || !cardScreen.classList.contains("hidden")) {
    startTour();
    return;
  }
  let attempts = 0;
  const tick = (): void => {
    attempts++;
    if (!cardScreen.classList.contains("hidden") || attempts > 30) {
      startTour();
    } else {
      setTimeout(tick, 100);
    }
  };
  tick();
}
